import { transformUserInfo } from '@utils/transform-response';

import type {
    AuthChangePassword,
    AuthForgotPassword,
    AuthGoogleLogin,
    AuthLogin,
    AuthRegistration,
    AuthUserInfo,
    Message,
    TwoFactorMethodEnum,
    TwoFaState,
} from '../../auth/store';

import { dashboardApi } from './dashboard-base-api';

const URL_PREFIX = 'webapi/account/';

export const dashboardAccountApiSlice = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        getUserInfo: build.query<AuthUserInfo, void>({
            query: () => `${URL_PREFIX}info`,
            transformResponse: (authInfo: AuthUserInfo) => transformUserInfo(authInfo),
        }),
        setupTwoFA: build.mutation<
            { secret: string; qr_code_url: string },
            { method: TwoFactorMethodEnum }
        >({
            query: () => ({
                url: `${URL_PREFIX}settings/2fa/totp/setup`,
                method: 'POST',
            }),
        }),
        disableTwoFA: build.mutation<void, { code: string }>({
            query: (body) => ({
                url: `${URL_PREFIX}settings/2fa/totp/disable`,
                method: 'POST',
                body,
            }),
        }),
        confirmTwoFASetup: build.mutation<
            { backup_codes: string[] },
            { code: string; method: TwoFactorMethodEnum }
        >({
            query: (body) => ({
                url: `${URL_PREFIX}settings/2fa/confirm`,
                method: 'POST',
                body,
            }),
        }),
        completeTwoFALogin: build.mutation<
            AuthUserInfo,
            {
                code: string;
                method: TwoFactorMethodEnum;
                auth_token: string;
                enabled_trusted_device: boolean;
            }
        >({
            query: (body) => ({
                url: `${URL_PREFIX}login/2fa`,
                method: 'POST',
                body,
            }),
        }),
        emailSendTwoFa: build.mutation<void, { email: string }>({
            query: (body) => ({
                url: `${URL_PREFIX}2fa/email/send`,
                method: 'POST',
                body,
                responseHandler: async (response) => {
                    try {
                        const text = await response.text();

                        return JSON.parse(text);
                    } catch {
                        return {};
                    }
                },
            }),
        }),
        uploadUserPhoto: build.mutation<{ message: string }, File>({
            query: (file) => {
                const formData = new FormData();

                formData.append('file', file);

                return {
                    method: 'PUT',
                    url: `${URL_PREFIX}settings/logo`,
                    formData: true,
                    body: formData,
                };
            },
        }),
        deleteUserPhoto: build.mutation<Message, void>({
            query: () => ({
                method: 'DELETE',
                url: `${URL_PREFIX}settings/logo`,
            }),
        }),
        changeUserFullName: build.mutation<Message, { full_name: string }>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}settings/change-name`,
                body,
            }),
        }),
        changeUserPassword: build.mutation<
            Message,
            { old_password?: string; new_password: string }
        >({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}reset`,
                body,
            }),
        }),
        registerAccount: build.mutation<Message | AuthUserInfo, AuthRegistration>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}register`,
                body,
            }),
        }),
        login: build.mutation<TwoFaState | AuthUserInfo, AuthLogin>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}login`,
                body,
            }),
        }),
        googleLogin: build.mutation<AuthUserInfo, AuthGoogleLogin>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}google/oauth`,
                body,
            }),
        }),
        resetPasswordRequest: build.mutation<Message, AuthForgotPassword>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}reset/request`,
                body,
            }),
        }),
        changePasswordByToken: build.mutation<Message, AuthChangePassword>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}reset`,
                body,
            }),
        }),

        logout: build.mutation<Message, void>({
            query: (body) => ({
                method: 'POST',
                url: `${URL_PREFIX}logout`,
                body,
            }),
        }),
    }),
});

export const {
    useLazyGetUserInfoQuery,
    useSetupTwoFAMutation,
    useDisableTwoFAMutation,
    useConfirmTwoFASetupMutation,
    useCompleteTwoFALoginMutation,
    useUploadUserPhotoMutation,
    useDeleteUserPhotoMutation,
    useChangeUserFullNameMutation,
    useChangeUserPasswordMutation,
    useRegisterAccountMutation,
    useResetPasswordRequestMutation,
    useChangePasswordByTokenMutation,
    useLoginMutation,
    useGoogleLoginMutation,
    useLogoutMutation,
    useEmailSendTwoFaMutation,
} = dashboardAccountApiSlice;
